import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, X, UserCheck, AlertCircle, RefreshCw, Search } from "lucide-react";

/* Recent Signups · Assign Credit
   ────────────────────────────────────────────────────────────────
   Lives in the admin sidebar under "Mike's Office". Two stacked
   panels:

   1. Pending — signups where the visitor typed a name/code that
      didn't auto-match anyone. Mike sees what they typed and the
      new signup's name+email. He can type the correct affiliate
      and tap ★ Assign Credit, OR dismiss the row if the visitor
      was just confused.

   2. Recent (last 30 days) — every signup, with its current
      credit. Click "Re-assign" to override any auto-attribution
      Mike disagrees with.

   This is the "make sure i am informed and i will ensure the
   right person gets the credit" panel Mike asked for. */

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";

export default function AttributionTab() {
  const [pending, setPending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});  // map of id → boolean
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/attribution/recent");
      setPending(r.data?.pending || []);
      setRecent(r.data?.recent || []);
    } catch (_e) {
      toast.error("Could not load recent signups.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const assign = async (row, isQueue) => {
    const key = `assign-${row.id}`;
    const input = row._typed?.trim?.() || "";
    if (!input) { toast.error("Type the affiliate's name, code, or email first."); return; }
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const payload = isQueue
        ? { queue_id: row.id, referred_user_id: row.referred_user_id, referrer_input: input }
        : { referred_user_id: row.id, referrer_input: input };
      const r = await api.post("/admin/attribution/assign", payload);
      toast.success(`★ Credited to ${r.data.referrer.name || r.data.referrer.code}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't assign that credit.");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  const dismiss = async (row) => {
    const key = `dismiss-${row.id}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post("/admin/attribution/dismiss", { queue_id: row.id });
      toast.success("Dismissed.");
      await load();
    } catch (_e) {
      toast.error("Couldn't dismiss.");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  // Allow inline typing — keeps each row's "Who should get credit?" text
  // local so the table is responsive even with 100+ rows.
  const updateTyped = (list, setList, id, val) => {
    setList(list.map((r) => (r.id === id ? { ...r, _typed: val } : r)));
  };

  const filteredRecent = query.trim()
    ? recent.filter((r) =>
        (r.email || "").toLowerCase().includes(query.toLowerCase()) ||
        (r.full_name || "").toLowerCase().includes(query.toLowerCase()) ||
        (r.referred_by_code || "").toLowerCase().includes(query.toLowerCase())
      )
    : recent;

  return (
    <div data-testid="attribution-tab" className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>★ ADMIN · ATTRIBUTION</div>
          <h1 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight" style={{ color: WINE }}>
            Recent Signups &amp; Assign Credit.
          </h1>
          <p className="text-sm text-foreground/70 mt-1">
            Anyone who signed up without a clear affiliate code lands here. Type the right person&apos;s name or code → tap ★ Assign Credit. They get the 50% commission.
          </p>
        </div>
        <button onClick={load} data-testid="attribution-refresh"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-[0.18em] hover:bg-white transition"
                style={{ background: "#EAF6FC", color: WINE, border: `2px solid ${O}55` }}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* PENDING QUEUE — top */}
      <section className="rounded-2xl bg-white shadow p-5" style={{ border: `3px solid ${O}` }} data-testid="attribution-pending">
        <div className="flex items-baseline gap-2 mb-3">
          <AlertCircle className="w-5 h-5 shrink-0" style={{ color: O }} />
          <h2 className="font-serif-display text-xl md:text-2xl uppercase" style={{ color: WINE }}>
            Needs Your Attention ({pending.length})
          </h2>
        </div>
        {loading ? (
          <div className="text-sm italic text-foreground/60">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="text-sm italic text-foreground/60">
            All clear — no unattributed signups waiting on you.
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((p) => (
              <div key={p.id} className="rounded-xl bg-white p-4 border-l-4" style={{ borderLeftColor: O }} data-testid={`attribution-pending-row-${p.id}`}>
                <div className="flex items-baseline gap-2 flex-wrap mb-2">
                  <div className="font-bold" style={{ color: WINE }}>{p.referred_full_name}</div>
                  <div className="text-sm text-foreground/70">·</div>
                  <div className="text-sm text-foreground/70">{p.referred_email}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold ml-auto px-2 py-0.5 rounded-full"
                       style={{ background: "#EAF6FC", color: WINE }}>
                    Tier: {p.tier_at_signup}
                  </div>
                </div>
                <div className="text-sm mb-3">
                  Visitor typed: <strong style={{ color: WINE }}>&ldquo;{p.raw_input}&rdquo;</strong> — no exact match.
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    placeholder="Affiliate's name, code, or email"
                    value={p._typed || ""}
                    onChange={(e) => updateTyped(pending, setPending, p.id, e.target.value)}
                    data-testid={`attribution-input-${p.id}`}
                    className="flex-1 min-w-[200px] px-3 py-2 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                    style={{ border: `2px solid ${O}55`, color: WINE }}
                  />
                  <button onClick={() => assign(p, true)}
                          disabled={busy[`assign-${p.id}`]}
                          data-testid={`attribution-assign-${p.id}`}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-[0.18em] text-white shadow disabled:opacity-50"
                          style={{ background: G }}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {busy[`assign-${p.id}`] ? "Saving…" : "★ Assign Credit"}
                  </button>
                  <button onClick={() => dismiss(p)}
                          disabled={busy[`dismiss-${p.id}`]}
                          data-testid={`attribution-dismiss-${p.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-[0.18em] hover:bg-white transition"
                          style={{ background: "#FFFFFF", color: WINE, border: `2px solid ${WINE}55` }}>
                    <X className="w-3.5 h-3.5" /> Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* RECENT — bottom */}
      <section className="rounded-2xl bg-white shadow p-5" style={{ border: `2px solid ${O}55` }} data-testid="attribution-recent">
        <div className="flex items-baseline gap-3 mb-3 flex-wrap">
          <UserCheck className="w-5 h-5 shrink-0" style={{ color: WINE }} />
          <h2 className="font-serif-display text-xl md:text-2xl uppercase" style={{ color: WINE }}>
            Recent Signups · Last 30 Days ({recent.length})
          </h2>
          <div className="ml-auto flex items-center gap-1.5 bg-[#FFFFFF] px-3 py-1.5 rounded-md" style={{ border: `2px solid ${O}33` }}>
            <Search className="w-3.5 h-3.5" style={{ color: O }} />
            <input
              placeholder="Search by name / email / code"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="attribution-search"
              className="bg-transparent outline-none text-sm w-48"
            />
          </div>
        </div>
        {loading ? (
          <div className="text-sm italic text-foreground/60">Loading…</div>
        ) : filteredRecent.length === 0 ? (
          <div className="text-sm italic text-foreground/60">No signups match that filter.</div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {filteredRecent.map((r) => {
              const isAttributed = !!r.referred_by_code;
              return (
                <div key={r.id}
                     className="rounded-lg p-3 flex flex-wrap items-center gap-3 text-sm"
                     style={{ background: isAttributed ? "#F4FBF4" : "#FFFFFF", border: `1px solid ${O}22` }}
                     data-testid={`attribution-recent-row-${r.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-bold" style={{ color: WINE }}>{r.full_name || r.email}</div>
                    <div className="text-xs text-foreground/60">{r.email} · {r.tier} · {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-xs">
                    {isAttributed ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full font-bold"
                            style={{ background: "#E8F5DD", color: G }}>
                        ★ {r.referred_by_code}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full italic text-foreground/55"
                            style={{ background: "#EAF6FC" }}>
                        No credit assigned
                      </span>
                    )}
                  </div>
                  <input
                    placeholder="Reassign to…"
                    value={r._typed || ""}
                    onChange={(e) => updateTyped(recent, setRecent, r.id, e.target.value)}
                    data-testid={`attribution-recent-input-${r.id}`}
                    className="px-2.5 py-1.5 rounded-md text-xs bg-white outline-none focus:ring-2 focus:ring-[#F39A1F] w-40"
                    style={{ border: `1px solid ${O}55`, color: WINE }}
                  />
                  <button onClick={() => assign(r, false)}
                          disabled={busy[`assign-${r.id}`] || !r._typed}
                          data-testid={`attribution-recent-assign-${r.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.12em] text-white shadow disabled:opacity-30"
                          style={{ background: WINE }}>
                    {isAttributed ? "Re-assign" : "Assign"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
